import React, { useState } from 'react';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { addDocumentContract } from '../../slices/document-contracts/documentContractsSlice';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface BlankDocumentModalProps {
  isBlankModalOpen: boolean;
  onClose: () => void;
}

export const BlankDocumentModal: React.FC<BlankDocumentModalProps> = ({
  // eslint-disable-next-line react/prop-types
  isBlankModalOpen,
  // eslint-disable-next-line react/prop-types
  onClose,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [documentCategory, setDocumentCategory] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleBlankDocumentCreation = (newname: string, category: string) => {
    const blankDocData = {
      data: {
        user: currentUser.id,
        name: newname,
        category: category,
      },
    };

    try {
      onClose();
      dispatch(addDocumentContract(blankDocData))
        .unwrap()
        .then((res: AIDocumentContractModel) => {
          navigate(`${ROUTES.DOCUMENT_VAULT}/${res.id}?initialTab=1&contractGenerated=true`);
        })
        .catch((error) => {
          // Handle any errors
          toast.error(t('messages.errorOccurred'));
        });
      // setIsBlankModalOpen(false); // Close the modal after the operation finishes
    } catch (error) {
      toast.error(t('messages.errorOccurred'));
    }
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (documentName === '' || documentCategory === '') {
      setShowErrors(true);
      return;
    }
    const formData = new FormData(e.currentTarget);
    const newDocumentName = formData.get('newDocumentName') as string;
    const category = formData.get('category') as string;
    handleBlankDocumentCreation(newDocumentName, category);
  };
  return (
    <Modal
      open={isBlankModalOpen}
      className='action-modal'
      sx={{
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        width: '50%',
        top: '0',
        left: '25%',
        margin: 0,
        padding: 0,
      }}
      onClose={() => {
        setShowErrors(false);
        onClose();
      }}
    >
      <Grid container>
        <Grid item xs={12} sm={11} md={11} lg={11}>
          <Card>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant={'h6'} gutterBottom>
                Create new document
              </Typography>
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    marginTop: '0.5rem',
                  }}
                >
                  <TextField
                    name='newDocumentName'
                    error={showErrors && documentName === ''}
                    placeholder='New document name'
                    label='Document name'
                    variant='outlined'
                    fullWidth
                    value={documentName}
                    required={true}
                    onChange={(e) => setDocumentName(e.target.value)}
                  />
                  <FormControl fullWidth>
                    <InputLabel required={true} id='demo-simple-select-label'>
                      Profile
                    </InputLabel>
                    <Select
                      labelId='demo-simple-select-label'
                      id='demo-simple-select'
                      name='category'
                      label='Category'
                      required={true}
                      error={showErrors && documentCategory === ''}
                      value={documentCategory}
                      onChange={(e) => setDocumentCategory(e.target.value)}
                    >
                      <MenuItem value='business'>Business</MenuItem>
                      <MenuItem value='employment'>Employment</MenuItem>
                      <MenuItem value='customers'>Customers</MenuItem>
                      <MenuItem value='suppliers'>Suppliers</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Grid container justifyContent='flex-end' gap={'8px'} mt={2}>
                  <Button
                    variant='contained'
                    onClick={() => {
                      setShowErrors(false);
                      onClose();
                    }}
                    className={'btn-default btn-action-basic btn-cancel'}
                  >
                    {t('buttons.goBack')}
                  </Button>

                  <Button
                    variant='contained'
                    type='submit'
                    className='btn-default btn-action-basic'
                    disabled={!documentName || !documentCategory}
                  >
                    {t('buttons.create')}
                  </Button>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
};
